import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, PageHeader, StatusBadge } from '../ui';
import { extractPageContent, generatePayloadPage, getPageTemplates } from '../../services/pageGenerationApi';
import './page-generation.css';

const FALLBACK_TEMPLATES = {
  'case-study': { pageType: 'caseStudy', allowedBlocks: ['content', 'textWithImage', 'featureGrid', 'mediaBlock', 'cta'] },
  'product-detail': { pageType: 'product', allowedBlocks: ['featureGrid', 'textWithImage', 'productPromoGrid', 'cta'] },
  'marketing-page': { pageType: 'marketing', allowedBlocks: ['content', 'featureGrid', 'promoStrip', 'mediaBlock', 'textWithImage', 'cta'] },
};

const formatError = (error) => {
  const responseData = error?.response?.data;
  const message = responseData?.detail || responseData?.error || error?.message;

  if (typeof message === 'string' && message.trim()) return message.trim();
  if (typeof message === 'object' && message) {
    const code = typeof message.code === 'string' ? ` [${message.code}]` : '';
    if (typeof message.error === 'string') return `${message.error}${code}`;
  }

  return 'The page import failed.';
};

const isDuplicateSourceError = (error) => formatError(error).includes('[DUPLICATE_SOURCE_URL]');

export default function PageGeneration() {
  const [templates, setTemplates] = useState(FALLBACK_TEMPLATES);
  const [sourceUrl, setSourceUrl] = useState('');
  const [templateKey, setTemplateKey] = useState('case-study');
  const [targetSlug, setTargetSlug] = useState('');
  const [extraction, setExtraction] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [duplicatePage, setDuplicatePage] = useState(null);

  useEffect(() => {
    let mounted = true;
    getPageTemplates()
      .then((response) => {
        if (mounted && response?.templates) setTemplates(response.templates);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const selected = templates[templateKey] || FALLBACK_TEMPLATES[templateKey];
  const pageType = selected?.pageType || 'caseStudy';
  const canSubmit = sourceUrl.trim() && templateKey && !busy;

  const sourceSummary = useMemo(() => {
    if (!extraction) return null;
    return [
      `${extraction.headings?.length || 0} headings`,
      `${extraction.paragraphs?.length || 0} paragraphs`,
      `${extraction.images?.length || 0} images`,
      `${extraction.links?.length || 0} links`,
    ].join(' · ');
  }, [extraction]);

  const handleExtract = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const response = await extractPageContent(sourceUrl.trim());
      setExtraction(response);
    } catch (requestError) {
      setError(formatError(requestError));
    } finally {
      setBusy(false);
    }
  };

  const handleGenerate = async (overrideExisting = false) => {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const response = await generatePayloadPage({
        sourceUrl: sourceUrl.trim(),
        pageType,
        templateKey,
        targetSlug: targetSlug.trim() || undefined,
        overrideExisting,
      });
      setResult(response);
    } catch (requestError) {
      if (!overrideExisting && isDuplicateSourceError(requestError)) {
        setDuplicatePage({ message: formatError(requestError) });
      } else {
        setError(formatError(requestError));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="page-generation">
      <PageHeader
        title="AI Pages"
        sub="Scan a public website page, map its content into an approved template, and create a Payload draft."
        crumbs={[{ label: 'Operations', to: '/jobs' }, { label: 'AI Pages' }]}
      />

      <div className="page-generation-grid">
        <Card title="Generate a Payload draft" desc="The content manager will review and publish the draft in Payload CMS.">
          <form className="page-generation-form" onSubmit={handleExtract}>
            <label className="oc-field">
              <span className="oc-field-label">Source page URL</span>
              <input
                className="oc-input"
                type="url"
                value={sourceUrl}
                onChange={(event) => setSourceUrl(event.target.value)}
                placeholder="https://www.waterco.com.au/case-studies/..."
                required
              />
            </label>
            <label className="oc-field">
              <span className="oc-field-label">Template type</span>
              <select className="oc-input" value={templateKey} onChange={(event) => setTemplateKey(event.target.value)}>
                {Object.entries(templates).map(([key, template]) => (
                  <option key={key} value={key}>{key} ({template.pageType})</option>
                ))}
              </select>
            </label>
            <label className="oc-field">
              <span className="oc-field-label">Target slug <span className="muted">(optional)</span></span>
              <input
                className="oc-input"
                value={targetSlug}
                onChange={(event) => setTargetSlug(event.target.value)}
                placeholder="third-space-battersea"
              />
            </label>
            <div className="page-generation-actions">
              <Button type="submit" disabled={!canSubmit || busy}>
                {busy ? 'Scanning…' : 'Scan page'}
              </Button>
              {extraction && (
                <Button type="button" disabled={busy || !extraction.quality?.sufficient} onClick={() => handleGenerate()}>
                  {busy ? 'Creating draft…' : 'Generate Payload draft'}
                </Button>
              )}
            </div>
          </form>
        </Card>

        <Card title="Template contract" desc="AI can only use these existing Payload blocks.">
          <div className="page-generation-template-meta">
            <Badge>{pageType}</Badge>
            <strong>{templateKey}</strong>
          </div>
          <div className="page-generation-blocks">
            {(selected?.allowedBlocks || []).map((block) => <span key={block}>{block}</span>)}
          </div>
          <p className="page-generation-note">The hero is stored separately and source images are uploaded to Payload Media before the draft is created.</p>
        </Card>
      </div>

      {duplicatePage && (
        <div className="page-generation-modal-backdrop" role="presentation">
          <section className="page-generation-modal" role="dialog" aria-modal="true" aria-labelledby="duplicate-page-title">
            <h2 id="duplicate-page-title">Page already generated</h2>
            <p>This source URL is already linked to a generated Payload page.</p>
            <p>Override it to replace its current CMS draft with newly scanned content. Its existing slug stays the same, and any published version remains unchanged until it is reviewed and published again.</p>
            <div className="page-generation-actions">
              <Button type="button" variant="secondary" onClick={() => setDuplicatePage(null)} disabled={busy}>Cancel</Button>
              <Button type="button" onClick={() => { setDuplicatePage(null); handleGenerate(true); }} disabled={busy}>
                Override draft
              </Button>
            </div>
          </section>
        </div>
      )}

      {error && <div className="page-generation-error" role="alert">{error}</div>}

      {extraction && (
        <Card title="Extraction result" desc={sourceSummary} className="page-generation-result">
          <div className="page-generation-excerpt">
            <strong>{extraction.title || 'Untitled page'}</strong>
            {extraction.metaDescription && <p>{extraction.metaDescription}</p>}
            {!extraction.quality?.sufficient && (
              <p className="page-generation-warning">The scan is incomplete. Draft generation is disabled until the page produces enough article content.</p>
            )}
            {extraction.renderAttempted && extraction.renderError && (
              <p className="page-generation-warning">Rendered-page fallback was unavailable: {extraction.renderError}</p>
            )}
          </div>
          <details>
            <summary>View extracted sections</summary>
            <pre>{JSON.stringify(extraction.sections, null, 2)}</pre>
          </details>
        </Card>
      )}

      {result?.import && (
        <Card title="Payload draft created" desc="Review and publish this page from Payload CMS." className="page-generation-success">
          <div className="page-generation-success-row">
            <StatusBadge status="succeeded" label={result.import.status || 'draft_generated'} />
            <span>Page ID: <strong>{result.import.id}</strong></span>
            <span>Slug: <strong>{result.import.slug}</strong></span>
          </div>
        </Card>
      )}
    </main>
  );
}
